import type { QueuedReason } from '@/lib/api/generated/model';

/**
 * The queue's vocabulary (spec 115, research R2).
 *
 * One module, because the card, the drawer, the Tasks page and the capacity meter all
 * describe the same wait, and four phrasings of it would be four answers to "why
 * hasn't this started?".
 */

const ORDINAL_RULES = new Intl.PluralRules('en', { type: 'ordinal' });
const ORDINAL_SUFFIX: Record<Intl.LDMLPluralRule, string> = {
  zero: 'th',
  one: 'st',
  two: 'nd',
  few: 'rd',
  many: 'th',
  other: 'th',
};

/** 1st, 2nd, 3rd, 4th, 11th, 21st — a place in line. */
export function ordinal(n: number): string {
  return `${n}${ORDINAL_SUFFIX[ORDINAL_RULES.select(n)]}`;
}

/**
 * Why one queued run waits, as a sentence — or `null` when the service did not say
 * (an older service), in which case nothing is claimed.
 *
 * `limit` is the workspace's enforced limit, already clamped by the ceiling, so the
 * number named is the one that is actually holding the run back.
 */
export function queuedReasonSentence(
  reason: QueuedReason | null | undefined,
  limit: number | null,
): string | null {
  switch (reason) {
    case 'workspace_limit':
      return limit === null
        ? 'This workspace is at its limit'
        : `This workspace runs ${limit} at a time`;
    case 'system_limit':
      return 'The system is busy — it will start as capacity frees';
    case 'admitting':
      return 'Starting…';
    default:
      return null;
  }
}

export interface WorkspaceLoad {
  queued: number;
  running: number;
  limit: number;
}

/** What a waiting card says it is doing (spec 118 FR-009). */
export const WAITING_FOR_A_SLOT = 'Waiting for a slot';

/**
 * The capacity meter's sentence (spec 118 R4): the board notice's rule, said once the
 * notice is gone. The workspace is the reason while it is full; with room in the
 * workspace, what holds its runs back can only be the installation.
 */
export function meterSentence({
  queued,
  running,
  limit,
}: WorkspaceLoad): string {
  if (queued === 0) return 'Nothing is waiting.';
  return running >= limit
    ? `This workspace runs ${limit} at a time. Waiting runs start by themselves, in this order.`
    : 'The system is busy — waiting runs start as capacity frees.';
}

/** What the capacity meter counts: the workspace's, or the installation's (spec 118). */
export interface Capacity {
  running: number;
  /** The enforced limit: the workspace's, or the installation's ceiling. */
  limit: number;
  queued: number;
}

/** "Runs at once: 3 of 5 running, 2 waiting" — the meter's whole meaning, in words. */
export function meterName({ running, limit, queued }: Capacity): string {
  const figures = `${String(running)} of ${String(limit)} running`;
  return `Runs at once: ${figures}${queued > 0 ? `, ${String(queued)} waiting` : ''}`;
}
